import React, { useMemo } from 'react';
import { Text, type TextT } from '@mentimeter/ragnar-ui/text';
import { Link as L } from '@mentimeter/ragnar-ui/link';
import { contrast } from '@mentimeter/ragnar-colors';
import { useRagnar } from '@mentimeter/ragnar-react';
import {
  StringParser,
  MathSvg,
  type ComponentsT,
} from '@mentimeter/string-parser';

const Bold = (props: TextT) => <Text {...defaultPropsBold} {...props} />;

export const defaultPropsBold: TextT = {
  fontWeight: 'semiBold',
  lineHeight: 'inherit',
  color: 'inherit',
  fontSize: 'inherit',
  fontFamily: 'inherit',
};

const Italic = (props: TextT) => (
  <Text
    fontStyle="italic"
    color="inherit"
    lineHeight="inherit"
    fontSize="inherit"
    fontFamily="inherit"
    {...props}
  />
);

const Code = (props: TextT) => (
  <Text
    fontFamily="monospace"
    lineHeight="inherit"
    color="inherit"
    fontSize="inherit"
    {...props}
  />
);

const Link = ({ href, ...props }: TextT & { href: string }) => {
  const {
    theme: {
      visualizationColors: { fillColors, textColor, backgroundColor },
    },
  } = useRagnar();

  const BAD_CONTRAST_THRESHOLD = 2;
  const color = useMemo(() => {
    if (!fillColors[0]) return textColor;
    return contrast(fillColors[0], backgroundColor) < BAD_CONTRAST_THRESHOLD
      ? textColor
      : fillColors[0];
  }, [fillColors, backgroundColor, textColor]);

  return (
    <L
      target="_blank"
      rel="noreferrer noopener nofollow"
      href={href}
      color={color}
    >
      <Text
        lineHeight="inherit"
        fontSize="inherit"
        color="inherit"
        {...props}
      />
    </L>
  );
};

const Math = ({ value }: { value: string }) => {
  return <MathSvg math={value} />;
};

export const stringParserComponents: ComponentsT = {
  Strong: Bold,
  Em: Italic,
  Code,
  Math,
  Link,
};

export const MarkdownText = ({
  source,
  disableLineBreaks,
  allowBlankLines,
  allowHyperlinks,
}: {
  source: string;
  disableLineBreaks: boolean;
  allowBlankLines?: boolean;
  allowHyperlinks?: boolean;
}) => (
  <StringParser
    components={stringParserComponents}
    source={source}
    disableLineBreaks={disableLineBreaks}
    allowBlankLines={Boolean(allowBlankLines)}
    allowHyperlinks={Boolean(allowHyperlinks)}
  />
);
